/**
 * The guest demo's own screens (AD-794 §6.3–§6.5, §7.1): the end card, the queue page, the
 * "full" and "try again" panels, the shared CTA block and the in-game demo banner. Words and
 * links come from `guestCta.ts` (pure, unit-tested); this file only lays them out in the
 * client's one centred-panel pattern (`.renderer-lost` → `.guest-panel`).
 *
 * Every link opens in a NEW TAB through `/api/guest/out/<target>` — a click never costs the
 * visitor their running demo or their queue place.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { TRADE_MARK_NOTICE } from '@great-cloud/shared/client';

import type { GuestCtaSource, GuestCtaTarget } from './guestApi.js';
import {
  deathCardCopy,
  endCardCopy,
  FOUNDER_FINE_PRINT,
  guestCtaBlock,
  queueCopy,
} from './guestCta.js';
import type { GuestCta } from './guestCta.js';
import { END_CONFIRM_ARM_MS, END_CONFIRM_COPY, END_CONFIRM_TIMEOUT_MS } from './guestEndConfirm.js';
import type { GuestEndReason } from './guestFlow.js';

interface ModeProps {
  registrationOpen: boolean;
  ctaTargets: readonly GuestCtaTarget[];
}

function OutLink({ cta, className }: { cta: GuestCta; className: string }): JSX.Element {
  return (
    <a
      className={className}
      href={cta.href}
      target="_blank"
      rel="noopener noreferrer"
      data-cta={cta.target}
      aria-label={`${cta.label} (opens in a new tab)`}
    >
      {cta.label}
    </a>
  );
}

/** The CTA block the end card and the queue page share (§6.5). */
export function GuestCtaBlockView({
  registrationOpen,
  ctaTargets,
  extra,
  lead,
  from,
  quiet,
}: ModeProps & {
  extra?: ReactNode;
  lead?: ReactNode;
  from?: GuestCtaSource;
  /** Every offer secondary: the card's own `lead` is the one primary action (the death card). */
  quiet?: boolean;
}): JSX.Element {
  const block = guestCtaBlock(registrationOpen, ctaTargets, undefined, from);
  const founder = block.secondary.some((c) => c.target === 'founder_interest');
  return (
    <div className="guest-ctas">
      {quiet ? (
        <div className="guest-cta-row">
          {lead}
          {block.primary ? <OutLink cta={block.primary} className="guest-cta secondary" /> : null}
          {block.secondary.map((c) => (
            <OutLink key={c.target} cta={c} className="guest-cta secondary" />
          ))}
          {extra}
        </div>
      ) : (
        <>
          {lead}
          {block.primary ? (
            <div className="guest-primary-offer">
              {block.primary.target === 'newsletter' ? (
                <p>
                  Monthly development updates, the latest devlog, playtest news and the announcement
                  when accounts open.
                </p>
              ) : null}
              <OutLink cta={block.primary} className="guest-cta primary" />
              {block.primary.target === 'newsletter' ? (
                <p className="guest-cta-fine">
                  Confirm your email to join. Unsubscribe at any time.
                </p>
              ) : null}
            </div>
          ) : null}
          {block.secondary
            .filter((c) => c.target !== 'devlog' && c.target !== 'website')
            .map((c) => (
              <div className="guest-secondary-offer" key={c.target}>
                {c.target === 'newsletter' ? (
                  <p className="guest-cta-fine">
                    Monthly development updates, devlogs and opportunities to take part. Confirm
                    your email to join; unsubscribe at any time.
                  </p>
                ) : null}
                <OutLink cta={c} className="guest-cta secondary" />
                {c.target === 'founder_interest' ? (
                  <p className="guest-cta-fine">
                    {FOUNDER_FINE_PRINT} This separate notification list is not a purchase or a
                    reservation.
                  </p>
                ) : null}
                {c.target === 'social_discord' ? (
                  <p className="guest-cta-fine">Share your demo feedback and follow development.</p>
                ) : null}
              </div>
            ))}
          <div className="guest-cta-row">
            {block.secondary
              .filter((c) => c.target === 'devlog' || c.target === 'website')
              .map((c) => (
                <OutLink key={c.target} cta={c} className="guest-cta secondary" />
              ))}
            {extra}
          </div>
        </>
      )}
      {quiet && founder ? <p className="guest-cta-fine">{FOUNDER_FINE_PRINT}</p> : null}
      {block.socials.length > 0 ? (
        <div className="guest-socials">
          <span className="guest-socials-label">Follow the road</span>
          {block.socials.map((c) => (
            <OutLink key={c.target} cta={c} className="guest-social" />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The end card (§6.4) — on turn-in, at the TTL, when the player leaves or the link drops. */
export function GuestEndCard({
  reason,
  registrationOpen,
  ctaTargets,
  onPlayAgain,
}: ModeProps & { reason: GuestEndReason; onPlayAgain: () => void }): JSX.Element {
  const copy = endCardCopy(registrationOpen, reason);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div className="guest-screen">
      <div
        className="guest-panel guest-end-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guest-end-heading"
        data-testid="guest-end-card"
      >
        <h2 id="guest-end-heading" ref={headingRef} tabIndex={-1}>
          {copy.heading}
        </h2>
        {copy.lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <GuestCtaBlockView
          registrationOpen={registrationOpen}
          ctaTargets={ctaTargets}
          extra={
            <button type="button" className="guest-cta secondary" onClick={onPlayAgain}>
              Play again
            </button>
          }
        />
        <p className="guest-note">
          {copy.note}{' '}
          <a
            href="https://thegreatcloudgame.life/Privacy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Privacy notice (opens in a new tab)"
          >
            Privacy notice
          </a>
        </p>
        <p className="guest-cta-fine">{TRADE_MARK_NOTICE}</p>
      </div>
    </div>
  );
}

/**
 * The death card (AD-833; design §5.6.3): over the dimmed, still-running world when the guest's
 * own body goes down. "Try again" leads (the held respawn at the waystone); the end card's own
 * offers follow — same targets, same order, same launch mode, each opening a new tab and counted
 * `from=death`. Nothing here ends the demo: the card waits for Try again (or the Menu's End demo,
 * which stays reachable around the panel).
 */
export function GuestDeathCard({
  registrationOpen,
  ctaTargets,
  retrying,
  onTryAgain,
}: ModeProps & { retrying: boolean; onTryAgain: () => void }): JSX.Element {
  const copy = deathCardCopy(retrying);
  return (
    <div className="guest-screen guest-death">
      <div
        className="guest-panel guest-death-card"
        role="dialog"
        aria-modal="false"
        aria-labelledby="guest-death-heading"
        data-testid="guest-death-card"
      >
        <h2 id="guest-death-heading">{copy.heading}</h2>
        <p>{copy.line}</p>
        <GuestCtaBlockView
          registrationOpen={registrationOpen}
          ctaTargets={ctaTargets}
          from="death"
          quiet
          lead={
            <button
              type="button"
              className="guest-cta primary guest-try-again"
              onClick={onTryAgain}
              disabled={retrying}
              data-testid="guest-try-again"
            >
              {copy.tryAgain}
            </button>
          }
        />
        <p className="guest-note">{copy.note}</p>
      </div>
    </div>
  );
}

/** The queue page (§7.1): the real position, an honest ETA or none, the shared CTA block. */
export function GuestQueueScreen({
  position,
  etaSeconds,
  registrationOpen,
  ctaTargets,
}: ModeProps & { position: number; etaSeconds: number | null }): JSX.Element {
  const copy = queueCopy(position, etaSeconds, registrationOpen);
  return (
    <div className="guest-screen">
      <div className="guest-panel" role="status" aria-live="polite" data-testid="guest-queue">
        <h2>{copy.heading}</h2>
        <p className="guest-position">{copy.position}</p>
        {copy.eta ? <p>{copy.eta}</p> : null}
        <p className="guest-note">{copy.hint}</p>
        <GuestCtaBlockView registrationOpen={registrationOpen} ctaTargets={ctaTargets} />
      </div>
    </div>
  );
}

/** A refusal the flow cannot resolve on its own, or the queue being full (§7.1 "go away"). */
export function GuestMessageScreen({
  heading,
  message,
  actionLabel,
  onAction,
  registrationOpen,
  ctaTargets,
}: ModeProps & {
  heading: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div className="guest-screen">
      <div className="guest-panel" role="alert" data-testid="guest-message">
        <h2>{heading}</h2>
        <p>{message}</p>
        <GuestCtaBlockView
          registrationOpen={registrationOpen}
          ctaTargets={ctaTargets}
          extra={
            actionLabel && onAction ? (
              <button type="button" className="guest-cta secondary" onClick={onAction}>
                {actionLabel}
              </button>
            ) : null
          }
        />
      </div>
    </div>
  );
}

/** The banner's phone wording (the top-right row is ~150 px wide on an upright iPhone). */
const BANNER_SHORT: Partial<Record<GuestCta['target'], string>> = {
  newsletter: 'Newsletter',
  register: 'Sign up',
};

/**
 * The thin in-game banner (§6.2): "Demo — nothing here is saved" + the mode's primary link. On a
 * phone layout both say it shorter and each stays on one line (the full wording wrapped to three
 * lines in an iPhone's narrow top-right row); the full wording stays as
 * visually-hidden text (and the link's accessible name), so screen readers say it whole.
 */
export function GuestDemoBanner({ registrationOpen, ctaTargets }: ModeProps): JSX.Element {
  const block = guestCtaBlock(registrationOpen, ctaTargets);
  const cta = block.primary;
  return (
    <div className="guest-banner interactive" data-testid="guest-banner">
      <span className="guest-banner-note">
        <span className="guest-banner-full">Demo — nothing here is saved</span>
        <span className="guest-banner-short" aria-hidden="true">
          Demo · not saved
        </span>
      </span>
      {cta ? (
        <a
          className="guest-banner-link"
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          data-cta={cta.target}
          aria-label={`${cta.label} (opens in a new tab)`}
        >
          <span className="guest-banner-full">{cta.label}</span>
          <span className="guest-banner-short" aria-hidden="true">
            {BANNER_SHORT[cta.target] ?? cta.label}
          </span>
        </a>
      ) : null}
    </div>
  );
}

/**
 * The End-demo confirm (§5.6.4; `guestEndConfirm.ts` says why it is built this way). Opened from
 * the Menu's End demo item. "Keep playing" leads and holds the focus; "End demo" is the second,
 * quieter control, ignored until it has armed (`endConfirmStep` judges; `data-ready` flips and the label dims), and
 * a tap outside the panel, Escape, or eight idle seconds all return to play.
 */
export function GuestEndConfirm({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const arm = setTimeout(() => setReady(true), END_CONFIRM_ARM_MS);
    const give = setTimeout(onCancel, END_CONFIRM_TIMEOUT_MS);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(arm);
      clearTimeout(give);
      window.removeEventListener('keydown', onKey);
    };
  }, [onCancel]);
  return (
    <div
      className="guest-screen guest-end-confirm-screen interactive"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="guest-end-confirm-backdrop"
    >
      <div
        className="guest-panel guest-end-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="guest-end-confirm-heading"
        data-testid="guest-end-confirm"
        data-ready={ready ? 'true' : 'false'}
      >
        <h2 id="guest-end-confirm-heading">{END_CONFIRM_COPY.heading}</h2>
        <p>{END_CONFIRM_COPY.line}</p>
        <div className="guest-cta-row">
          <button
            type="button"
            className="guest-cta primary"
            onClick={onCancel}
            autoFocus
            data-testid="guest-end-cancel"
          >
            {END_CONFIRM_COPY.cancel}
          </button>
          <button
            type="button"
            className={`guest-cta secondary guest-end-go${ready ? '' : ' arming'}`}
            aria-disabled={!ready}
            onClick={onConfirm}
            data-testid="guest-end-confirm-go"
          >
            {END_CONFIRM_COPY.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
